import { arrayMove } from '@dnd-kit/sortable';
import { ClientId, FieldConfig, FieldData } from './Field';
import { SectionData } from './Section';
import { randomInt } from '../../../utils/number';

export type Action =
  | { type: 'section_add' }
  | { type: 'section_remove'; sectionId: ClientId }
  | { type: 'section_up'; sectionId: ClientId }
  | { type: 'section_down'; sectionId: ClientId }
  | { type: 'section_title_change'; sectionId: ClientId; title: string }
  | { type: 'field_add'; sectionId: ClientId; copy: boolean }
  | {
      type: 'field_remove';
      sectionId: ClientId;
      fieldId: ClientId;
    }
  | {
      type: 'field_order_change';
      sectionId: ClientId;
      fromId: ClientId;
      toId: ClientId;
    }
  | {
      type: 'field_config_change';
      sectionId: ClientId;
      fieldId: ClientId | 'ALL';
      fieldConfig: FieldConfig;
    }
  | {
      type: 'field_title_change';
      sectionId: ClientId;
      fieldId: ClientId;
      title: string;
    }
  | { type: 'reset'; newState: SectionData[] };

export function reducer(state: SectionData[], action: Action): SectionData[] {
  switch (action.type) {
    case 'section_add':
      return handleSectionAdd(state);
    case 'section_remove':
      return handleSectionRemove(state, action.sectionId);
    case 'section_up':
      return handleSectionUp(state, action.sectionId);
    case 'section_down':
      return handleSectionDown(state, action.sectionId);
    case 'section_title_change':
      return handleSectionTitleChange(state, action.sectionId, action.title);
    case 'field_add':
      return handleFieldAdd(state, action.sectionId, action.copy);
    case 'field_remove':
      return handleFieldRemove(state, action.sectionId, action.fieldId);
    case 'field_order_change':
      return handleFieldOrderChange(
        state,
        action.sectionId,
        action.fromId,
        action.toId,
      );
    case 'field_config_change':
      return handleFieldConfigChange(
        state,
        action.sectionId,
        action.fieldId,
        action.fieldConfig,
      );
    case 'field_title_change':
      return handleFieldTitleChange(
        state,
        action.sectionId,
        action.fieldId,
        action.title,
      );
    case 'reset':
      return action.newState;
  }
}

function handleSectionAdd(sections: SectionData[]) {
  return [...sections, makeEmptySection()];
}

function handleSectionRemove(sections: SectionData[], sectionId: ClientId) {
  return sections.filter((section) => section.cId !== sectionId);
}

function handleSectionUp(sections: SectionData[], sectionId: ClientId) {
  const fromIdx = sections.findIndex((section) => section.cId === sectionId);
  const toIdx = fromIdx - 1;

  return arrayMove(sections, fromIdx, toIdx);
}

function handleSectionDown(sections: SectionData[], sectionId: ClientId) {
  const fromIdx = sections.findIndex((section) => section.cId === sectionId);
  const toIdx = fromIdx + 1;

  return arrayMove(sections, fromIdx, toIdx);
}

function handleSectionTitleChange(
  sections: SectionData[],
  sectionId: ClientId,
  title: string,
) {
  return sections.map((section) => {
    if (section.cId !== sectionId) return section;

    return { ...section, name: title };
  });
}

function handleFieldAdd(
  sections: SectionData[],
  sectionId: ClientId,
  copy: boolean,
) {
  return sections.map((section) => {
    if (section.cId !== sectionId) return section;

    const newField = makeEmptyField();
    if (copy) {
      const template = section.fields[0];
      newField.label = template.label;
      newField.fieldType = template.fieldType;
      newField.max = template.max;
    }

    return {
      ...section,
      fields: [...section.fields, newField],
    };
  });
}

function handleFieldRemove(
  sections: SectionData[],
  sectionId: ClientId,
  fieldId: ClientId,
) {
  return sections.map((section) => {
    if (section.cId !== sectionId) return section;

    return {
      ...section,
      fields: section.fields.filter((field) => field.cId !== fieldId),
    };
  });
}

function handleFieldOrderChange(
  sections: SectionData[],
  sectionId: ClientId,
  fromId: ClientId,
  toId: ClientId,
) {
  return sections.map((section) => {
    if (section.cId !== sectionId) return section;

    const fields = section.fields;
    const oldIndex = fields.findIndex((v) => v.cId === fromId);
    const newIndex = fields.findIndex((v) => v.cId === toId);

    return {
      ...section,
      fields: arrayMove(section.fields, oldIndex, newIndex),
    };
  });
}

function handleFieldConfigChange(
  sections: SectionData[],
  sectionId: ClientId,
  fieldId: ClientId | 'ALL',
  newConfig: FieldConfig,
) {
  return updateField(sections, sectionId, fieldId, {
    label: newConfig.label,
    fieldType: newConfig.fieldType,
    max: newConfig.max,
  });
}

function handleFieldTitleChange(
  sections: SectionData[],
  sectionId: ClientId,
  fieldId: ClientId,
  value: string,
) {
  return updateField(sections, sectionId, fieldId, { title: value });
}

function updateField(
  sections: SectionData[],
  sectionId: ClientId,
  fieldId: ClientId | 'ALL',
  obj: Partial<FieldData>,
) {
  return sections.map((section) => {
    if (section.cId !== sectionId) return section;

    const newFields = section.fields.map((field) => {
      if (fieldId != 'ALL' && field.cId !== fieldId) return field;

      return { ...field, ...obj };
    });

    return { ...section, fields: newFields };
  });
}

export function initSections() {
  return [makeEmptySection()];
}

function makeEmptySection(): SectionData {
  return {
    sId: null,
    cId: randomInt(),
    name: '',
    fields: [makeEmptyField()],
  };
}

function makeEmptyField(): FieldData {
  return { sId: null, cId: randomInt(), title: '' };
}
